import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  ElementRef,
  inject,
  computed,
  signal,
} from '@angular/core';
import { toIsoCurrency } from '../../../../../../shared/utils/xtm-currency.utils';
import { FormatCurrencyPipe } from '../../../../../../shared/pipes/format-currency.pipe';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Estimate, Quote, VendorOrder } from '../../../../../../shared/models/vendor-order.model';
import { VendorService } from '../../../../../../shared/services/vendor.service';
import { SpinnerComponent } from '../../../../../../shared/components/az-spinner/spinner.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

export interface SelectQuoteModalData {
  order: VendorOrder;
  estimate: Estimate;
  hasLowestRate: boolean;
}

export type SelectQuoteModalResult =
  | { action: 'confirmed'; jobUid: string }
  | { action: 'cancelled' };

type ModalStep = 'confirm' | 'purchase-order' | 'success';

@Component({
  selector: 'app-select-quote-modal',
  standalone: true,
  imports: [FormatCurrencyPipe, FormsModule, SpinnerComponent],
  templateUrl: './select-quote-modal.component.html',
  styleUrl: './select-quote-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectQuoteModalComponent {
  private readonly dialogRef = inject(MatDialogRef<SelectQuoteModalComponent>);
  private readonly vendorService = inject(VendorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elRef = inject(ElementRef);
  readonly data: SelectQuoteModalData = inject(MAT_DIALOG_DATA);

  private recommendedRect: DOMRect | null = null;

  readonly step = signal<ModalStep>('confirm');
  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly purchaseOrderId = signal('');
  readonly poTouched = signal(false);

  readonly vendorName = computed(() => this.data.estimate.name ?? 'Unknown Vendor');

  readonly isDtpJob = computed(() => !!this.data.order.isDtpJob);

  readonly quote = computed<Quote | null>(() => {
    const est = this.data.estimate;
    return est.quote?.normal ?? est.quote?.rushed ?? null;
  });

  readonly deliveryType = computed(() => {
    const est = this.data.estimate;
    if (est.quote?.rushed && !est.quote?.normal) return 'Rushed';
    return 'Standard';
  });

  readonly deliveryDate = computed(() => {
    const est = this.data.estimate;
    const type = this.deliveryType();
    const raw = type === 'Rushed'
      ? est.estimatedDeliveryDates?.rushed?.estimatedDeliveryDate
      : est.estimatedDeliveryDates?.normal?.estimatedDeliveryDate;
    return raw ? this.formatDate(raw) : (est.deadline ? this.formatDate(est.deadline) : '--');
  });

  readonly quoteId = computed(() => this.data.order.jobNumber ?? '--');

  readonly currency = computed(() => {
    const est = this.data.estimate;
    return toIsoCurrency(est.currency ?? this.quote()?.currency);
  });

  readonly costBreakdown = computed<{ concept: string; cost: number }[]>(() => {
    const q = this.quote();
    if (!q) return [];
    const items: { concept: string; cost: number }[] = [];
    if (q.costs?.length) {
      return q.costs;
    }
    if (q.dtpCost) items.push({ concept: 'Desktop publishing', cost: q.dtpCost });
    if (q.translationCost) items.push({ concept: 'File preparation', cost: q.translationCost });
    if (q.managementCost) items.push({ concept: 'Management', cost: q.managementCost });
    if (q.rushedFees) items.push({ concept: 'Rushed fees', cost: q.rushedFees });
    return items;
  });

  readonly totalCost = computed(() => {
    const q = this.quote();
    if (q?.totalCost != null) return q.totalCost;
    const est = this.data.estimate;
    return est.priceMax ?? est.priceMin ?? 0;
  });

  readonly recommendedPOTotal = computed(() => {
    return Math.ceil(this.totalCost() * 1.15 * 100) / 100;
  });

  readonly bufferAmount = computed(() => {
    return this.recommendedPOTotal() - this.totalCost();
  });

  onCancel(): void {
    this.dialogRef.close({ action: 'cancelled' } as SelectQuoteModalResult);
  }

  goBack(): void {
    this.transitionStep('confirm');
  }

  onConfirmQuote(): void {
    this.transitionStep('purchase-order');
  }

  private transitionStep(to: ModalStep): void {
    const el = this.elRef.nativeElement.querySelector('.select-quote-modal__recommended');
    if (el) {
      this.recommendedRect = el.getBoundingClientRect();
    }

    this.step.set(to);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.animateRecommendedPO());
    });
  }

  private animateRecommendedPO(): void {
    if (!this.recommendedRect) return;

    const el = this.elRef.nativeElement.querySelector('.select-quote-modal__recommended') as HTMLElement;
    if (!el) return;

    const newRect = el.getBoundingClientRect();
    const dx = this.recommendedRect.x - newRect.x;
    const dy = this.recommendedRect.y - newRect.y;

    el.style.transform = `translate(${dx}px, ${dy}px)`;
    el.style.transition = 'none';

    requestAnimationFrame(() => {
      el.style.transition = 'transform 300ms ease-out';
      el.style.transform = 'translate(0, 0)';

      el.addEventListener('transitionend', () => {
        el.style.transform = '';
        el.style.transition = '';
      }, { once: true });
    });

    this.recommendedRect = null;
  }

  onPoKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowed.includes(event.key) || (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key)) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onPoPaste(event: ClipboardEvent): void {
    const paste = event.clipboardData?.getData('text') ?? '';
    if (!/^\d+$/.test(paste)) {
      event.preventDefault();
    }
  }

  onPoDrop(event: DragEvent): void {
    event.preventDefault();
  }

  onPoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/\D/g, '');
    if (input.value !== sanitized) {
      input.value = sanitized;
      this.purchaseOrderId.set(sanitized);
    }
  }

  onConfirmPO(): void {
    const poId = this.purchaseOrderId().trim();
    if (!poId) return;

    this.loading.set(true);
    this.errorMessage.set(null);

    const { order, estimate, hasLowestRate } = this.data;

    const vendorId = estimate.workflow_uid ?? estimate.vendorId!;

    const selectData = {
      vendorId,
      vendorName: estimate.name,
      hasLowestRate,
    };

    this.vendorService
      .selectVendorOrder(order.jobUid!, selectData)
      .pipe(
        switchMap(() =>
          this.vendorService.authorizeVendorOrder(order.jobUid!, {
            purchaseOrderId: poId,
            vendorPrice: this.totalCost(),
            vendorId,
            vendorName: estimate.name,
            jobCreatedIn: order.jobCreatedIn,
          }).pipe(
            catchError((err) =>
              this.vendorService
                .selectVendorOrder(order.jobUid!, { vendorId: null, referenceVendorId: vendorId })
                .pipe(switchMap(() => throwError(() => err)))
            )
          )
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.step.set('success');
        },
        error: () => {
          this.loading.set(false);
          this.errorMessage.set('Failed to authorize order. Please try again.');
        },
      });
  }

  onDone(): void {
    this.dialogRef.close({
      action: 'confirmed',
      jobUid: this.data.order.jobUid!,
    } as SelectQuoteModalResult);
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '--';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }
}
